import { useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { LoginActionButton } from "@adaltra/ui";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { LabeledField } from "./LabeledField";
import { IconoMorfo } from "./IconoMorfo";
import { authApi } from "../api/endpoints";

function PasswordAdornment({ visible, onToggle }) {
  const { t } = useTranslation();

  return (
    <InputAdornment position="end">
      <IconButton
        size="small"
        edge="end"
        aria-label={t(visible ? "auth.hidePassword" : "auth.showPassword")}
        onClick={onToggle}
      >
        {/* El ojo se transforma: la raya se dibuja encima en vez de
            aparecer un icono distinto de golpe. */}
        <IconoMorfo trazo={visible ? "ojoTachado" : "ojo"} size={21} />
      </IconButton>
    </InputAdornment>
  );
}

const EMPTY_REGISTRATION = {
  fullName: "",
  email: "",
  phone: "",
  companyName: "",
  taxId: "",
  password: "",
};

export function PortalRegistrationForm({ onRegistered, onPendingChange }) {
  const { t, i18n } = useTranslation();
  const [values, setValues] = useState(EMPTY_REGISTRATION);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (event) =>
    setValues((current) => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    onPendingChange?.(true);
    setError(null);
    try {
      await authApi.register({
        ...values,
        email: values.email.trim().toLowerCase(),
        phone: values.phone || null,
        taxId: values.taxId || null,
        locale: i18n.resolvedLanguage?.split("-")[0] ?? "es",
      });
      onRegistered();
    } catch (apiError) {
      setError(apiError.code ?? "errors.unexpected");
    } finally {
      setSubmitting(false);
      onPendingChange?.(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      noValidate
      sx={{ width: "100%", maxWidth: 590 }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {t(error, { defaultValue: t("errors.unexpected") })}
        </Alert>
      )}

      {/* Rejilla de dos columnas, como el `register-grid` original. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 1.75,
        }}
      >
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <LabeledField
            label={t("auth.fullName")}
            required
            dense
            autoComplete="name"
            value={values.fullName}
            onChange={update("fullName")}
          />
        </Box>

        <LabeledField
          label={t("auth.email")}
          type="email"
          required
          dense
          autoComplete="email"
          value={values.email}
          onChange={update("email")}
        />
        <LabeledField
          label={t("auth.phone")}
          dense
          autoComplete="tel"
          value={values.phone}
          onChange={update("phone")}
        />

        <LabeledField
          label={t("auth.companyName")}
          required
          dense
          autoComplete="organization"
          value={values.companyName}
          onChange={update("companyName")}
        />
        <LabeledField
          label={t("auth.taxId")}
          dense
          value={values.taxId}
          onChange={update("taxId")}
        />

        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <LabeledField
            label={t("auth.password")}
            type={showPassword ? "text" : "password"}
            required
            dense
            autoComplete="new-password"
            helperText={t("auth.passwordHint")}
            value={values.password}
            onChange={update("password")}
            slotProps={{
              input: {
                endAdornment: (
                  <PasswordAdornment
                    visible={showPassword}
                    onToggle={() => setShowPassword((value) => !value)}
                  />
                ),
              },
            }}
          />
        </Box>
      </Box>

      <LoginActionButton
        type="submit"
        fullWidth
        pending={submitting}
        sx={{ mt: { xs: 0.75, sm: 1.5 } }}
      >
        {submitting ? (
          <>
            <CircularProgress size={17} color="inherit" sx={{ mr: 1.25 }} />
            {t("common.sending")}
          </>
        ) : (
          t("auth.createAccountAction")
        )}
      </LoginActionButton>


    </Box>
  );
}
